import {
    useQuery,
    UseQueryOptions,
    useMutation,
    UseMutationOptions,
    useQueryClient,
} from '@tanstack/react-query'
import { pipedriveAPI, AddDealProductDto } from '../api/pipedriveApi'

// Query Keys helpers
const keys = {
    all: ['pipedrive'] as const,
    products: (params?: { start?: number; limit?: number }) =>
        [...keys.all, 'products', params] as const,
    search: (term: string, start?: number, limit?: number) =>
        [...keys.all, 'products', 'search', { term, start, limit }] as const,
    deal: (id: number) => [...keys.all, 'deal', id] as const,
}

export const usePipedriveProducts = (
    params?: { start?: number; limit?: number },
    options?: UseQueryOptions<
        any,
        unknown,
        any,
        ReturnType<typeof keys.products>
    >
) => {
    return useQuery({
        queryKey: keys.products(params),
        queryFn: () => pipedriveAPI.listProducts(params),
        staleTime: 1000 * 60, // 1 min
        ...options,
    })
}

export const useSearchPipedriveProducts = (
    term: string | undefined,
    params?: { start?: number; limit?: number },
    options?: UseQueryOptions<any, unknown, any, ReturnType<typeof keys.search>>
) => {
    return useQuery({
        queryKey: keys.search(term || '', params?.start, params?.limit),
        queryFn: () =>
            pipedriveAPI.searchProducts({
                term: term || '',
                start: params?.start,
                limit: params?.limit,
            }),
        enabled: Boolean(term && term.length > 1),
        ...options,
    })
}

export const usePipedriveDeal = (
    id: number | undefined,
    options?: UseQueryOptions<any, unknown, any, ReturnType<typeof keys.deal>>
) => {
    return useQuery({
        queryKey: keys.deal(id || 0),
        queryFn: () => pipedriveAPI.getDeal(id as number),
        enabled: typeof id === 'number',
        ...options,
    })
}

// Mutations
export const useAddDealProduct = (
    id: number,
    options?: UseMutationOptions<any, unknown, AddDealProductDto>
) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (payload: AddDealProductDto) =>
            pipedriveAPI.addDealProduct(id, payload),
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: keys.deal(id) })
            options?.onSuccess?.(data, variables, context)
        },
        ...options,
    })
}

export const useUpdateDealProduct = (
    id: number,
    productAttachmentId: number,
    options?: UseMutationOptions<any, unknown, AddDealProductDto>
) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (payload: AddDealProductDto) =>
            pipedriveAPI.updateDealProduct(id, productAttachmentId, payload),
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: keys.deal(id) })
            options?.onSuccess?.(data, variables, context)
        },
        ...options,
    })
}

export const useRemoveDealProduct = (
    id: number,
    productAttachmentId: number,
    options?: UseMutationOptions<any, unknown, void>
) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: () =>
            pipedriveAPI.removeDealProduct(id, productAttachmentId),
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: keys.deal(id) })
            options?.onSuccess?.(data as any, variables, context)
        },
        ...options,
    })
}

export const pipedriveQueryKeys = keys
